// src/components/admin/scheduling/components/WeekBar.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./WeekBar.css";
import { DAYS_ORDER } from "../constants";
import { FaRegTrashAlt } from "react-icons/fa";
import { FiSettings, FiMinus, FiPlus, FiX } from "react-icons/fi";
import { IoDuplicate } from "react-icons/io5";

function clampToMidnight(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function isSameDay(a, b) {
  if (!a || !b) return false;
  return +clampToMidnight(a) === +clampToMidnight(b);
}
function isBetweenInclusive(d, a, b) {
  const t = +clampToMidnight(d);
  return t >= +clampToMidnight(a) && t <= +clampToMidnight(b);
}
export function dayKeyFromDate(d) {
  return ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"][d.getDay()] || null;
}

export function resolveCalendarNavigation({ viewMode, pickedDate, isDateClosed }) {
  const normalized = clampToMidnight(pickedDate);

  if (viewMode === "month") {
    return { date: normalized, viewMode: "month" };
  }

  if (viewMode === "week") {
    return { date: normalized, viewMode: "week" };
  }

  const dk = dayKeyFromDate(normalized);
  if (dk && (!isDateClosed || !isDateClosed(normalized))) {
    return { date: normalized, viewMode: dk };
  }

  return { date: normalized, viewMode: "week" };
}

function startOfMonthLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonthLocal(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfWeekMondayLocal(d) {
  const x = clampToMidnight(d);
  const monIndex = (x.getDay() + 6) % 7;
  return addDays(x, -monIndex);
}
function buildMonthGrid(focusMonth) {
  const start = startOfMonthLocal(focusMonth);
  const startDow = start.getDay();
  const gridStart = addDays(start, -startDow);
  const rows = [];
  let cur = gridStart;
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) { row.push(cur); cur = addDays(cur, 1); }
    rows.push(row);
  }
  // Drop any row where every day falls outside the focus month (e.g. an all-next-month 6th week)
  const fm = focusMonth.getMonth();
  const fy = focusMonth.getFullYear();
  const filteredRows = rows.filter((row) =>
    row.some((d) => d.getMonth() === fm && d.getFullYear() === fy)
  );
  return { rows: filteredRows, monthStart: start, monthEnd: endOfMonthLocal(focusMonth) };
}
function monthLabel(d) {
  return new Intl.DateTimeFormat("en-US", { month: "long" }).format(d);
}
function yearLabel(d) {
  return new Intl.DateTimeFormat("en-US", { year: "numeric" }).format(d);
}
function formatShortNumericDate(d) {
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}
function getIsoWeekNumber(dateInput) {
  const d = new Date(Date.UTC(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function isInteractiveEscapeTarget(el) {
  if (!el) return false;
  const tag = el?.tagName?.toLowerCase?.();
  if (tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable) return true;
  if (typeof el.closest !== "function") return false;
  return !!el.closest(
    [
      "[role='textbox']",
      "[role='combobox']",
      "[role='listbox']",
      "[role='option']",
      "[contenteditable='true']",
    ].join(",")
  );
}

export default function WeekBar({
  isOpen,
  onClose,
  onToggleOpen,
  disableEscapeToggle = false,

  weekStart,
  weekEnd,
  tutors,
  selectedTutorUid,
  onSelectTutor,

  onPrevWeek,
  onNextWeek,
  onToday,
  onOpenSettings,
  showWeekBarByDefault = true,
  onToggleWeekBarDefaultVisibility,
  onDuplicateWeek,
  onOpenTrash,

  uiZoom = 1,
  onUiZoomChange,

  viewMode,
  onSetViewMode,
  currentDayDate,
  onPrevDay,
  onNextDay,

  // kept for compat, not used in UI
  monthCursor,
  yearCursor,
  onPrevMonth,
  onNextMonth,
  onPrevYear,
  onNextYear,

  isMasterMode = false,
  onToggleMaster,
  titleOverride,
  subtitleOverride,

  onPickDate,
  isDateClosed,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsButtonRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const [settingsPos, setSettingsPos] = useState(null);

  const updateSettingsPosition = () => {
    if (!settingsButtonRef.current) return;
    const rect = settingsButtonRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const viewportPad = 8;
    const left = Math.max(
      viewportPad,
      Math.min(window.innerWidth - menuWidth - viewportPad, rect.right - menuWidth)
    );
    setSettingsPos({
      position: "fixed",
      top: rect.bottom + 8,
      left,
      zIndex: 260,
      minWidth: menuWidth,
    });
  };

  // Close settings dropdown on outside click + keep menu anchored
  useEffect(() => {
    if (!settingsOpen) return;
    const handle = (e) => {
      const inButton = settingsButtonRef.current?.contains(e.target);
      const inMenu = settingsMenuRef.current?.contains(e.target);
      if (!inButton && !inMenu) {
        setSettingsOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    const handleReposition = () => updateSettingsPosition();
    updateSettingsPosition();
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", handleEsc);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", handleEsc);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [settingsOpen]);

  // Toggle drawer on Escape, but do not interfere with inputs, menus, or the settings menu.
  useEffect(() => {
    const handle = (e) => {
      if (e.key !== "Escape") return;
      if (disableEscapeToggle) return;
      if (isInteractiveEscapeTarget(e.target) || isInteractiveEscapeTarget(document.activeElement)) return;
      if (settingsOpen) return;
      onToggleOpen?.();
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onToggleOpen, settingsOpen, disableEscapeToggle]);

  const isDayKey = DAYS_ORDER.includes(viewMode);
  const primaryView = isDayKey ? "day" : viewMode === "month" ? "month" : "week";

  const selectedDate = useMemo(() => {
    if (primaryView === "day" && currentDayDate) return clampToMidnight(currentDayDate);
    if (primaryView === "month" && monthCursor) return clampToMidnight(monthCursor);
    return clampToMidnight(weekStart || new Date());
  }, [primaryView, currentDayDate, monthCursor, weekStart]);

  const headerMain = useMemo(() => {
    // Master mode: parent always provides a titleOverride string ("Master Schedule")
    if (isMasterMode) return titleOverride || "Master Schedule";
    // Day view: show the specific weekday name
    if (primaryView === "day" && currentDayDate) {
      return currentDayDate.toLocaleDateString("en-US", { weekday: "long" });
    }
    // Month view: show month name
    if (primaryView === "month" && monthCursor) {
      return monthCursor.toLocaleDateString("en-US", { month: "long" });
    }
    // Week view: show ISO week number (e.g. "Week 22")
    const ws = clampToMidnight(weekStart || startOfWeekMondayLocal(selectedDate));
    return `Week ${getIsoWeekNumber(ws)}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleOverride, isMasterMode, primaryView, currentDayDate, weekStart, viewMode]);

  const headerSub = useMemo(() => {
    // Master mode
    if (isMasterMode) return subtitleOverride !== undefined ? subtitleOverride : "";
    // Day view: show full date (e.g. "May 28, 2026")
    if (primaryView === "day" && currentDayDate) {
      return currentDayDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    }
    // Month view: show year
    if (primaryView === "month" && monthCursor) {
      return String(monthCursor.getFullYear());
    }
    // Week view: show compact numeric date range
    const ws = weekStart || startOfWeekMondayLocal(selectedDate);
    const we = addDays(ws, 6);
    return `${formatShortNumericDate(ws)} - ${formatShortNumericDate(we)}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtitleOverride, isMasterMode, primaryView, currentDayDate, weekStart, selectedDate, viewMode]);

  const navHandlers = useMemo(() => {
    if (primaryView === "day")   return { onPrev: onPrevDay   || (() => {}), onNext: onNextDay   || (() => {}) };
    if (primaryView === "month") return { onPrev: onPrevMonth || (() => {}), onNext: onNextMonth || (() => {}) };
    return { onPrev: onPrevWeek || (() => {}), onNext: onNextWeek || (() => {}) };
  }, [primaryView, onPrevDay, onNextDay, onPrevWeek, onNextWeek, onPrevMonth, onNextMonth]);

  const handleSelectPrimary = (nextPrimary) => {
    if (!onSetViewMode) return;
    if (nextPrimary === "day") {
      if (isDayKey) return;
      const todayMid = clampToMidnight(new Date());
      const ws = clampToMidnight(weekStart);
      const we = clampToMidnight(weekEnd || addDays(ws, 6));
      let candidateDate = isBetweenInclusive(todayMid, ws, we) ? todayMid : ws;
      let candidateDayKey = dayKeyFromDate(candidateDate);
      let attempts = 0;
      while ((!candidateDayKey || (isDateClosed && isDateClosed(candidateDate))) && attempts < 7) {
        candidateDate = addDays(candidateDate, 1);
        candidateDayKey = dayKeyFromDate(candidateDate);
        attempts += 1;
      }
      onSetViewMode(candidateDayKey || "monday");
      return;
    }
    if (nextPrimary === "month") {
      onSetViewMode("month");
      return;
    }
    onSetViewMode("week");
  };

  const weekHighlightStart = useMemo(() => {
    if (primaryView !== "week") return null;
    return clampToMidnight(weekStart || startOfWeekMondayLocal(selectedDate));
  }, [primaryView, weekStart, selectedDate]);

  const weekHighlightEnd = useMemo(() => {
    if (!weekHighlightStart) return null;
    return addDays(weekHighlightStart, 6);
  }, [weekHighlightStart]);

  const [calendarSelectionDate, setCalendarSelectionDate] = useState(() => clampToMidnight(selectedDate));
  useEffect(() => {
    if (primaryView === "day" && currentDayDate) {
      setCalendarSelectionDate(clampToMidnight(currentDayDate));
      return;
    }
    if (primaryView === "week" && weekHighlightStart) {
      setCalendarSelectionDate(clampToMidnight(weekHighlightStart));
      return;
    }
    if (primaryView === "month") {
      setCalendarSelectionDate(clampToMidnight(monthCursor || selectedDate));
      return;
    }
    setCalendarSelectionDate(clampToMidnight(selectedDate));
  }, [primaryView, currentDayDate, monthCursor, selectedDate, weekHighlightStart]);

  const [calMonth, setCalMonth] = useState(() => startOfMonthLocal(selectedDate));
  useEffect(() => {
    const nextMonth = primaryView === "month" && monthCursor ? startOfMonthLocal(monthCursor) : startOfMonthLocal(selectedDate);
    setCalMonth(nextMonth);
  }, [monthCursor, primaryView, selectedDate]);

  const cal = useMemo(() => buildMonthGrid(calMonth), [calMonth]);

  const handlePickDate = (d) => {
    const picked = clampToMidnight(d);
    setCalendarSelectionDate(picked);
    const next = resolveCalendarNavigation({ viewMode, pickedDate: picked, isDateClosed });

    if (viewMode === "month") {
      setCalMonth(startOfMonthLocal(picked));
      if (onPickDate) {
        onPickDate(picked);
        return;
      }
      if (onSetViewMode) onSetViewMode("month");
      return;
    }

    if (viewMode === "week") {
      setCalMonth(startOfMonthLocal(picked));
      if (onPickDate) {
        onPickDate(picked);
        return;
      }
      if (onSetViewMode) onSetViewMode("week");
      return;
    }

    if (onPickDate) { onPickDate(next.date); return; }
    if (onSetViewMode) onSetViewMode(next.viewMode);
  };

  const zoomPct = Math.round((uiZoom || 1) * 100);

  return (
    <>
      <aside
        className={`schedDrawer ${isOpen ? "isOpen" : ""}`}
        aria-label="Schedule sidebar"
      >
        {/* ── Scrollable body (header + calendar) — teacher row lives OUTSIDE so dropdown can escape ── */}
        <div className="sbScrollContent">

          {/* ── Header ── */}
          <div className="sbTop">
            <div className="sbDrawerHeadRow">
              <div className="sbDrawerTitles">
                <div className="sbWeekTitle">{headerMain}</div>
                {headerSub ? <div className="sbWeekRange">{headerSub}</div> : null}
              </div>
              <button type="button" className="sbCloseBtn" onClick={onClose} aria-label="Close sidebar">
                <FiX size={18} />
              </button>
            </div>

            {!isMasterMode ? (
              <>
                <div className="sbNavRow" aria-label="Navigation controls">
                  <button type="button" className="sbCircleBtn" onClick={navHandlers.onPrev} aria-label="Previous">‹</button>
                  <button type="button" className="sbTodayBtn" onClick={onToday}>Today</button>
                  <button type="button" className="sbCircleBtn" onClick={navHandlers.onNext} aria-label="Next">›</button>
                </div>

                <div className="sbSegmented" role="tablist" aria-label="View toggle">
                  <button
                    type="button"
                    className={`sbSegBtn ${primaryView === "day" ? "isActive" : ""}`}
                    onClick={() => handleSelectPrimary("day")}
                  >
                    Day
                  </button>
                  <button
                    type="button"
                    className={`sbSegBtn ${primaryView === "week" ? "isActive" : ""}`}
                    onClick={() => handleSelectPrimary("week")}
                  >
                    Week
                  </button>
                  {!isMasterMode && (
                    <button
                      type="button"
                      className={`sbSegBtn ${primaryView === "month" ? "isActive" : ""}`}
                      onClick={() => handleSelectPrimary("month")}
                    >
                      Month
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>

          {/* ── Mini Calendar ── */}
          {!isMasterMode ? (
            <div className="sbCalendarCard" aria-label="Mini calendar">
              <div className="sbCalHeader">
                <button
                  type="button"
                  className="sbCalNavBtn"
                  onClick={() => {
                    if (primaryView === "month") {
                      const nextMonth = startOfMonthLocal(addDays(calMonth, -1));
                      setCalMonth(nextMonth);
                      if (onPrevMonth) onPrevMonth();
                      else if (onSetViewMode) onSetViewMode("month");
                      return;
                    }

                    setCalMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
                  }}
                  aria-label="Previous month"
                >‹</button>
                <div className="sbCalMonthLabel">
                  <span className="sbCalMonth">{monthLabel(calMonth)}</span>
                  <span className="sbCalYear">{yearLabel(calMonth)}</span>
                </div>
                <button
                  type="button"
                  className="sbCalNavBtn"
                  onClick={() => {
                    if (primaryView === "month") {
                      const nextMonth = startOfMonthLocal(addDays(endOfMonthLocal(calMonth), 1));
                      setCalMonth(nextMonth);
                      if (onNextMonth) onNextMonth();
                      else if (onSetViewMode) onSetViewMode("month");
                      return;
                    }

                    setCalMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
                  }}
                  aria-label="Next month"
                >›</button>
              </div>

              <div className="sbCalDowRow" aria-hidden="true">
                {["S","M","T","W","T","F","S"].map((x, i) => (
                  <div key={i} className="sbCalDow">{x}</div>
                ))}
              </div>

              <div className="sbCalGrid">
                {cal.rows.flat().map((d, idx) => {
                  const isClosed = isDateClosed ? !!isDateClosed(d) : (d.getDay() === 0 || d.getDay() === 5);
                  const inMonth = d.getMonth() === calMonth.getMonth();
                  const isSelected = !isClosed && isSameDay(d, calendarSelectionDate);
                  const isMonthSelected = primaryView === "month" && inMonth && !isClosed;
                  const inWeekRange =
                    primaryView === "week" && weekHighlightStart && weekHighlightEnd
                      ? isBetweenInclusive(d, weekHighlightStart, weekHighlightEnd) && !isClosed
                      : false;
                  const isRangeStart = inWeekRange && isSameDay(d, weekHighlightStart);
                  const isRangeEnd   = inWeekRange && isSameDay(d, weekHighlightEnd);

                  const cls = [
                    "sbCalDay",
                    inMonth ? "" : "isOutside",
                    isClosed ? "isClosed" : "",
                    isSelected ? "isSelected" : "",
                    isMonthSelected ? "isMonthSelected" : "",
                    inWeekRange ? "isInRange" : "",
                    isRangeStart ? "isRangeStart" : "",
                    isRangeEnd ? "isRangeEnd" : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <button
                      key={`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${idx}`}
                      type="button"
                      className={cls}
                      aria-label={d.toDateString()}
                      aria-disabled={isClosed}
                      onClick={() => handlePickDate(d)}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

        </div>{/* end sbScrollContent */}

        {/* ── Teacher dropdown + Settings gear (outside scroll body so dropdown can escape) ── */}
        <div className="sbSection sbTeacherRow">
          <select
            className="sbTeacherSelect"
            value={selectedTutorUid || ""}
            onChange={(e) => onSelectTutor && onSelectTutor(e.target.value || "")}
            aria-label="Teacher filter"
          >
            <option value="">All Teachers</option>
            {(tutors || []).map((t) => (
              <option key={t.id} value={t.id}>{t.name || t.email || t.id}</option>
            ))}
          </select>

          {/* Settings dropdown */}
          <div className="sbSettingsWrap">
            <button
              type="button"
              className="sbIconBtn sbSettingsBtn"
              ref={settingsButtonRef}
              onClick={() => {
                setSettingsOpen((v) => !v);
                requestAnimationFrame(updateSettingsPosition);
              }}
              aria-label="Schedule settings"
              aria-expanded={settingsOpen}
            >
              <FiSettings size={16} />
            </button>

            {settingsOpen && createPortal(
              <div
                ref={settingsMenuRef}
                className="schedDropdownMenu schedDropdownMenu--floating"
                role="menu"
                style={settingsPos || undefined}
              >
                {/* Master / Duplicate / Trash */}
                <div className="schedDropdownGroup">
                  <button
                    type="button"
                    className={`schedDropdownItem ${isMasterMode ? "schedDropdownItem--active" : ""}`}
                    role="menuitem"
                    onClick={() => { onToggleMaster?.(); setSettingsOpen(false); }}
                  >
                    {isMasterMode ? "Exit Master Mode" : "Master Schedule"}
                  </button>
                  <button
                    type="button"
                    className="schedDropdownItem"
                    role="menuitem"
                    onClick={() => { onDuplicateWeek?.(); setSettingsOpen(false); }}
                  >
                    <IoDuplicate size={14} style={{ marginRight: 6 }} />
                    Duplicate Week
                  </button>
                  <button
                    type="button"
                    className="schedDropdownItem schedDropdownItem--warn"
                    role="menuitem"
                    onClick={() => { onOpenTrash?.(); setSettingsOpen(false); }}
                  >
                    <FaRegTrashAlt size={13} style={{ marginRight: 6 }} />
                    Trash
                  </button>
                </div>

                <div className="schedDropdownSep" />

                {/* Settings modal */}
                <div className="schedDropdownGroup">
                  <div className="schedDropdownToggleRow" role="menuitemcheckbox" aria-checked={showWeekBarByDefault}>
                    <span className="schedDropdownToggleLabel">Week Bar Default</span>
                    <button
                      type="button"
                      className={`schedSwitch ${showWeekBarByDefault ? "isOn" : "isOff"}`}
                      onClick={() => onToggleWeekBarDefaultVisibility?.(!showWeekBarByDefault)}
                      aria-label={`Week bar default ${showWeekBarByDefault ? "on" : "off"}`}
                    >
                      <span className="schedSwitchThumb" />
                    </button>
                    <span className="schedDropdownToggleState">{showWeekBarByDefault ? "ON" : "OFF"}</span>
                  </div>

                  <button
                    type="button"
                    className="schedDropdownItem"
                    role="menuitem"
                    onClick={() => { onOpenSettings?.(); setSettingsOpen(false); }}
                  >
                    <FiSettings size={14} style={{ marginRight: 6 }} />
                    Settings
                  </button>
                </div>

                <div className="schedDropdownSep" />

                {/* Zoom controls */}
                <div className="schedDropdownZoomRow">
                  <span className="schedDropdownZoomLabel">Zoom: {zoomPct}%</span>
                  <div className="schedDropdownZoomBtns">
                    <button
                      type="button"
                      className="schedDropdownZoomBtn"
                      onClick={() => onUiZoomChange?.(Number(Math.max(0.2, uiZoom - 0.1).toFixed(1)))}
                      aria-label="Zoom out"
                    >
                      <FiMinus size={14} />
                    </button>
                    <button
                      type="button"
                      className="schedDropdownZoomBtn"
                      onClick={() => onUiZoomChange?.(Number(Math.min(3.0, uiZoom + 0.1).toFixed(1)))}
                      aria-label="Zoom in"
                    >
                      <FiPlus size={14} />
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
